import { gsap } from "gsap";
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// --- IMPORTANT FIX: REGISTER PLUGINS FIRST ---
// This ensures GSAP knows about ScrollTrigger and can use it.
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

// ---------------------------------------------

if (!customElements.get('video-animation')) {
  customElements.define(
    'video-animation',
    class VideoAnimation extends HTMLElement {
      timeline = null;
      videoAdded = false;
      resizeObserver = null;
      section_id = this.dataset.sectionId;

      selectors = {
        videoFrameWrapper: this.querySelector(`#VideoFrame-Wrapper-${this.section_id}`),
        videoFrame: this.querySelector(`#VideoFrame-${this.section_id}`),
        videoWrapper: this.querySelector(`#VideoWrapper-${this.section_id}`),
        video: this.querySelector(`#Video-${this.section_id}`),
      }

      constructor() {
        super();

        this.debouncedUpdate = debounce(this.updateAnimation.bind(this), 50);

        !this.animateVideo && this.setupVideoPosition();

        if (!this.selectors.video || !this.selectors.videoFrame) return;
        this.setupScrollAnimation();
        this.setupResizeObserver();
      }

      setupVideoPosition() {
        const videoWrapperElement = this.selectors.videoWrapper;
        const triggerElement = this.selectors.videoFrame;

        // Get the bounding rectangle of the trigger element
        const videoWrapperElementRect = videoWrapperElement?.getBoundingClientRect();
        const triggerElementRect = triggerElement?.getBoundingClientRect();

        // Position and size the video wrapper element to fit within the trigger frame
        gsap.set(videoWrapperElement, {
          x: () => triggerElementRect.left - videoWrapperElementRect.left,
          y: () => (videoWrapperElementRect.top - triggerElementRect.top) * -1,
          width: triggerElementRect.width,
          height: triggerElementRect.height
        });

        window.addEventListener('load', () => {
          if (this.videoAdded) return;
          this.setVideo();
        });
      }

      setVideo() {
        const videoWrapper = this.selectors.videoWrapper;
        const templateVideo = this.selectors.videoFrameWrapper.querySelector('template').content.firstElementChild?.cloneNode(true);

        if (!templateVideo || !videoWrapper) return;

        const videoStart = (isMobile = false) => {
          templateVideo.setAttribute('playsinline', '');
          templateVideo.setAttribute('webkit-playsinline', '');

          if (isMobile) {
            templateVideo.muted = false;
            templateVideo.removeAttribute('muted');
          } else {
            templateVideo.muted = true;
          }

          videoWrapper.appendChild(templateVideo);
          templateVideo.load();

          const playPromise = templateVideo.play();
          if (playPromise !== undefined) {
            playPromise.catch(error => {
              console.error("Playback failed. Logic likely blocked by browser policy:", error);
            });
          }
        };

        // Mobile Path
        if (window.innerWidth < 750) {
          videoWrapper.addEventListener('click', () => {
            if (this.videoAdded) return;
            videoStart(true); // pass true to unmute
            this.videoAdded = true;
          }, { once: true });
        }
        // Desktop Path
        else {
          videoStart(false);
          this.videoAdded = true;
        }
      }

      setupScrollAnimation() {
        if (this.timeline) {
          this.timeline.kill();
          this.timeline = null;
        }

        const videoWrapperElement = this.selectors.videoWrapper;
        const triggerWrapperElement = this.selectors.videoFrameWrapper;
        const videoElement = this.selectors.video;
        const triggerElement = this.selectors.videoFrame;

        // Recalculate all bounding rectangles for the most current values
        const videoElementRect = videoElement?.getBoundingClientRect();
        const videoWrapperElementRect = videoWrapperElement?.getBoundingClientRect();
        const triggerElementRect = triggerElement?.getBoundingClientRect();

        // Calculate travel distance and initial position
        const videoTravelDistance = videoWrapperElementRect.top - triggerElementRect.top;
        this.setAttribute('data-distance', videoTravelDistance);

        const initialX = triggerElementRect.left - videoWrapperElementRect.left;
        const initialY = videoTravelDistance * -1;
        const initialWidth = triggerElementRect.width;
        const initialHeight = triggerElementRect.height;

        // --- 1. Define the 'from' (initial/starting) state ---
        const fromVars = {
          x: initialX,
          y: initialY,
          width: `${initialWidth}`,
          height: `${initialHeight}`
        };

        // --- 2. Define the 'to' (ending/target) state ---
        const toVars = {
          x: 0,
          y: 0,
          width: `${videoWrapperElementRect.width}`,
          height: `${videoWrapperElementRect.height}`,
          ease: "power2.out"
        };

        // --- 3. Create the timeline and attach ScrollTrigger ---
        this.timeline = gsap.timeline({
          scrollTrigger: {
            trigger: triggerWrapperElement,
            start: `top 170px`,
            scrub: 0.5,
            // markers: true, // You can toggle this for debugging
          }
        });

        // Add the fromTo animation to the timeline
        this.timeline.fromTo(videoElement, fromVars, toVars);
      }

      setupResizeObserver() {
        if (!this.selectors.videoFrameWrapper) return;

        this.resizeObserver = new ResizeObserver(() => {
          this.debouncedUpdate();
        });

        // Start observing the desired element
        this.resizeObserver.observe(this);
      }

      updateAnimation() {
        // 1. Re-run the setup. This function kills the old timeline first.
        this.setupScrollAnimation();

        // 2. Crucial: Recalculates all ScrollTriggers' start/end positions based on the new layout
        ScrollTrigger.refresh();
      }

      get animateVideo() {
        return this.dataset.animateVideo === 'true';
      }
    }
  )
}